import { SuretyAccountId, SuretyQuoteStatus, suretyQuoteStatusDef } from "@inrev/common";
import { useFetchSuretyAccount } from "@portal/api/account";
import { BondRequestPreview } from "@portal/api/bond-request/agent";
import { useFetchBondRequestPreviews } from "@portal/api/previews";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { checkboxFilter, FilterableDataTable } from "@portal/components/ui/FilterableDataTable";
import { getStatusTagByStatus, statusDisplayLabelMap } from "@portal/components/ui/StatusTag";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import type { ColumnDef, ColumnFiltersState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { FC, useMemo } from "react";
import { bondRequestTypeLabelMap } from "../../../../../constants";
import { formatBondAmount, formatDateForCsv, formatEmpty } from "../../../../../utils";

type AccountBondRequestsTableProps = {
	accountId: SuretyAccountId;
};

const DEFAULT_STATUS_FILTERS: ColumnFiltersState = [
	{
		id: "status",
		value: suretyQuoteStatusDef
			.filter((status) => status !== SuretyQuoteStatus.closed)
			.map((status) => statusDisplayLabelMap[status] ?? status),
	},
];

const BASE_COLUMNS: ColumnDef<BondRequestPreview>[] = [
	{
		id: "status",
		accessorFn: (row) => statusDisplayLabelMap[row.status] ?? row.status,
		header: "Status",
		filterFn: checkboxFilter,
		meta: { headerFilterType: "checkbox" },
		cell: (props) => getStatusTagByStatus(props.row.original.status),
		size: 190,
		minSize: 190,
	},
	{
		id: "type",
		accessorFn: ({ suretySubType }) =>
			formatEmpty(suretySubType ? bondRequestTypeLabelMap[suretySubType] : undefined),
		header: "Type",
		filterFn: checkboxFilter,
		meta: { headerFilterType: "checkbox" },
		size: 130,
		minSize: 75,
	},
	{
		id: "principal_name",
		accessorFn: ({ principalName }) => formatEmpty(principalName),
		header: "Principal",
		filterFn: checkboxFilter,
		meta: { headerFilterType: "checkbox" },
		size: 180,
		minSize: 130,
	},
	{
		id: "amount",
		header: "Amount",
		accessorFn: ({ amount }) => amount ?? 0,
		cell: (context) => formatBondAmount(context.row.original.amount),
		size: 130,
		minSize: 130,
	},
	{
		id: "description",
		accessorFn: ({ description }) => formatEmpty(description),
		header: "Description",
		size: 145,
		minSize: 130,
	},
	{
		id: "submittedAt",
		accessorFn: ({ submittedAt }) =>
			submittedAt !== undefined ? DateTime.fromISO(submittedAt).toJSDate() : undefined,
		cell: (props) =>
			props.row.original.submittedAt !== undefined
				? DateTime.fromISO(props.row.original.submittedAt).toLocaleString(DateTime.DATE_MED)
				: "--",
		header: "Submitted",
		size: 122,
		minSize: 122,
		enableGlobalFilter: false,
		sortingFn: "datetime",
		meta: { csvFormatter: formatDateForCsv },
	},
];

const getColumns = (isMinimalUIEnabled: boolean): ColumnDef<BondRequestPreview>[] =>
	BASE_COLUMNS.map((column) =>
		column.id === "description"
			? {
					id: "description",
					accessorFn: (row: BondRequestPreview) =>
						formatEmpty(isMinimalUIEnabled ? row.name : row.description),
					header: isMinimalUIEnabled ? "Project Name" : "Description",
					size: 145,
					minSize: 130,
				}
			: column,
	);

export const AccountBondRequestsTable: FC<AccountBondRequestsTableProps> = ({ accountId }) => {
	const { suretyAccount, suretyAccountIsLoading, suretyAccountError, refetchSuretyAccount } =
		useFetchSuretyAccount(accountId);

	const {
		bondRequestPreviews,
		bondRequestPreviewsIsLoading,
		bondRequestPreviewsError,
		refetchBondRequestPreviews,
	} = useFetchBondRequestPreviews();
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const columns = useMemo(() => getColumns(isMinimalUIEnabled), [isMinimalUIEnabled]);

	const isLoading = suretyAccountIsLoading || bondRequestPreviewsIsLoading;

	const accountQuotes = useMemo(() => {
		if (!bondRequestPreviews) return undefined;
		return bondRequestPreviews.filter((quote) => quote.accountId === accountId);
	}, [accountId, bondRequestPreviews]);

	if (suretyAccountError) {
		return (
			<ReportedRetryableLoadError
				error={suretyAccountError}
				resource="account"
				onRetry={refetchSuretyAccount}
			/>
		);
	}

	if (!suretyAccountIsLoading && !suretyAccount) {
		return <CenteredSpinner />;
	}

	if (bondRequestPreviewsError) {
		return (
			<ReportedRetryableLoadError
				error={bondRequestPreviewsError}
				resource="bond requests"
				onRetry={refetchBondRequestPreviews}
			/>
		);
	}

	return (
		<FilterableDataTable
			name="account_bond_requests_v2"
			columnDefs={columns}
			data={isLoading ? undefined : accountQuotes}
			defaultColumnFilters={DEFAULT_STATUS_FILTERS}
			getRowUrl={(row) => `/accounts/${row.accountId}/requests/${row.id}`}
			loadingMessage="Loading bond requests..."
			enableExportToCSV
			minHeight={400}
			maxHeight={400}
		/>
	);
};
